import { Injectable } from '@angular/core';

import { LayerApi } from '../api/layer-api';
//import { NodeType } from '../classes/tree/IBaseNode';
import { NearbyAssetNode } from '../classes/tree/nodes/NearbyAssetNode';
import { Tree } from '../classes/tree/tree';
import { BufferOptions } from '../components/buffer/buffer.config';
import { environment } from '../environment';
import { BufferService } from '../services/buffer.service';
import { DocumentService } from '../services/document.service';
import { LayerService } from '../services/layer.service';
import { SpatialQueryService } from '../services/spatialQuery.service';
import { ToSqlStringService } from '../services/to-sql-string.service';
import { IBuffer, IDocumentSource, ILayerColumn, ILayerTableChild, IMapcoreGraphicsLayer, IQuery, ITableColumn, MapCoreDocument, MapCoreLayer, QueryableMapCoreLayer } from '../types';

@Injectable({
    providedIn: 'root'
})
export class NearbyService {

    constructor(
        private readonly bufferService: BufferService,
        private readonly layerApi: LayerApi,
        private readonly layerService: LayerService,
        private readonly spatialQueryService: SpatialQueryService,
        private readonly toSqlStringService: ToSqlStringService,
        private readonly documentService: DocumentService
    ) {
        this.getApplicationNearbyAssets(environment.appId);
    }

    public maxRecordsFetched = 100000;
    public nearbyQueryName = 'NEARBY';

    public bufferColor: string = 'rgba(199, 135, 40, 0.75)';
    public bufferOutlineColor: string = '#c73828';

    public readonly nearbyLayers = {
        selection: 'Nearby Selection',
        assets: 'Nearby Assets',
        highlight: 'Nearby Highlight',
        mapPin: 'Nearby Pin'
    };

    private readonly nearbyTemplateFunction = 'NEARBY';

    public async getDocumentSources(node: Tree<NearbyAssetNode>): Promise<IDocumentSource[]> {
        let request;
        const data = node.data;
        if (data.record && !data.documentLoaded) {
            // TODO: Performance: Can these two calls be done earlier or its results cached for each layer in hierarchy?
            //       Both functions could fetch docSources for all layers in hierarchy with one call...
            if (node.parent.data.layerId) {
                request = await this.documentService.getLayerDocSources([node.parent.data.layerId]);
            } else {
                request = await this.documentService.getTableDocSources([node.parent.data.tableId]);
            }
        }
        return request ? request[0].docSources : [];
    }

    public async getDocuments(node: Tree<NearbyAssetNode>, sources: IDocumentSource[]): Promise<MapCoreDocument[]> {
        let documents = [];
        if (sources.length) {
            const feature = this.getFeatureData(node);
            // TODO: Is this call limited to 101 documents or 999? Remove the limit?
            documents = await this.documentService.getDocuments(feature, sources);
        }
        return documents;
    }

    private getFeatureData(node: Tree<NearbyAssetNode>): any {
        const data = node.data;
        const attributes = data.columnList.map((columnName, idx) => {
            return { columnName, value: data.record[idx] };
        });
        return { attributes };
    }

    public addDocumentNodes(node: Tree<NearbyAssetNode>, documents: MapCoreDocument[]) {
        const indent = 2;
        if (documents && documents.length) {
            documents.forEach(doc => {
                const name = `${doc.fileName}`;
                node.addChild(new NearbyAssetNode({ name, description: JSON.stringify(doc, undefined, indent), record: doc }));
            });
            const names = Object.keys(node.children[0].data.record);
            node.data.columns = names.map(name => {
                return { name, alias: this.documentService.ecsAliases[name] };
            });
            node.data.documentLoaded = true;
            node.data.enabled = true;
        }
    }

    public addRecordNodes(node: Tree<NearbyAssetNode>, columnList: string[], records) {
        const card = node.data.templates ? node.data.templates.filter(t => t.type === 'CARD' && t.functionType === this.nearbyTemplateFunction)[0] : null;
        const list = node.data.templates ? node.data.templates.filter(t => t.type === 'LIST' && t.functionType === this.nearbyTemplateFunction)[0] : null;
        const header = node.data.templates ? node.data.templates.filter(t => t.type === 'TITLE' && t.functionType === this.nearbyTemplateFunction)[0] : null;
        const deeplink = node.data.templates ? node.data.templates.filter(t => t.type === 'DEEPLINK' && t.functionType === this.nearbyTemplateFunction)[0] : null;

        records.forEach(record => {
            // TODO: Create a better default card template than stringify.
            const name = card ? this.applyTemplate(card.format, card.layout, columnList, record) : JSON.stringify(record);
            const description = list ?
                this.applyTemplate(list.format, list.layout, columnList, record) :
                columnList.filter(c => c !== 'GEOMETRY').map((c, i) => `${c.replace(/[-_.]/g, ' ')}: ${record[i]}`).join('\n');
            const title = header ? this.applyTemplate(header.format, header.layout, columnList, record) : name;
            const link = deeplink ? this.applyTemplate(deeplink.format, deeplink.layout, columnList, record) : null;
            const recordNode = new NearbyAssetNode({ id: node.data.id, name, title, description, link, columnList, record });
            recordNode.enabled = true;
            node.addChild(recordNode);
        });
        return;
    }

    // TODO: Move the following code a common location (service?) usable by all of MC2
    private applyTemplate(format: string, layout: string, columnList: string[], values: string[]) {
        switch (format) {
            case 'TEXT':
                const regex = /\$\{(\w+)\}/g;
                const fieldStart = 2;
                return layout.replace(regex, x => {
                    const idx = columnList.indexOf(x.slice(fieldStart, -1));
                    return (idx >= 0) ? values[idx] : '';
                });
                break;
            default:
                return layout;
        }
    }

    nearbyAssetsPromise: Promise<ILayerTableChild[]>;
    public async getApplicationNearbyAssets(appId: number, overwriteExistingValues: boolean = false): Promise<ILayerTableChild[]> {
        if (!this.nearbyAssetsPromise || overwriteExistingValues) {
            this.nearbyAssetsPromise = this.layerApi.getApplicationNearbyAssets(appId)
                .then(apps => {
                    const assets = apps[0].nearbyAssets.map(a => <ILayerTableChild>{
                            id: a.id,
                            parentId: a.parentId,
                            relationshipName: a.relationshipName,
                            layerId: a.layerId,
                            tableId: a.tableId,
                            parentJoinKey: a.parentJoinKey,
                            joinKey: a.joinKey,
                            templates: a.templates,
                            // TODO: Implement logic to handle * for columns; Not required for Nearby tool
                            columnList: !a.columnList || a.columnList.trim() === '*' ? [] : a.columnList.split(',').map(x => x.trim())
                    });
                    return assets;
                });
        }
        return this.nearbyAssetsPromise;
    }

    public getNearbyLayer(title: string): IMapcoreGraphicsLayer {
        const layers = this.layerService.mapLayers.graphics.filter(x => x.title === title);
        return layers.length ? layers[0] : null;
    }

    public async setNearbyLayer(title: string, graphics: __esri.Graphic[], removeLayer: boolean = true): Promise<IMapcoreGraphicsLayer> {
        const layer = this.getNearbyLayer(title) || await this.layerService.addGraphicsLayer({ title, hideInLegend: true });

        if (removeLayer) {
            layer.removeAll();
        }

        if (graphics.length) {
            layer.addMany(graphics);
        }

        // TODO: The title strings are in nearby.component. Should turn into a type and use that both places?
        if (title === 'Nearby Pin') {
            this.layerService.moveLayerToTheTop(layer);
        } else if (title === 'Nearby Selection') {
            // TODO: This move to bottom might move it under polygons so that it can't be seen. Perhaps
            //       this layer need to be on top but below the other nearby utility layers.
            this.layerService.moveLayerToTheBottom(layer);
        }

        return layer;
    }

    public async removeNearbyLayer(title: string) {
        const layer = this.getNearbyLayer(title);
        if (layer) {
            layer.destroy();
        }
    }

    public async getBuffers(distance: number, distanceUnit: __esri.LinearUnits, geometry: __esri.Geometry[] = []): Promise<IBuffer[]> {
        let buffers: IBuffer[];
        const bufferOptions: BufferOptions = {
            unionResults: true,
            distance,
            distanceUnit,
            areaUnit: 'square-miles',
            fillType: 'solid',
            fillColor: this.bufferColor,
            // opacity: 0.1,
            outlineType: 'solid',
            outlineColor: this.bufferOutlineColor,
            lineWidth: 2
        };
        buffers = await this.bufferService.getGeometryBuffers(geometry, bufferOptions);
        return buffers;
    }

    public async performSpatialQuery(layer: MapCoreLayer, selection: MapCoreLayer): Promise<IQuery> {
        if (layer) {
            const results = await this.spatialQueryService.spatialQueryLayers(true, [layer], [selection]);
            return this.spatialQueryService.getGuidQuery(results[0].guid, this.nearbyQueryName);
        }
        return null;
    }

    public getJoinQuery(parentRecordNode: Tree<NearbyAssetNode>, node: Tree<NearbyAssetNode>): IQuery {
        const idx = parentRecordNode.data.columnList.indexOf(node.data.parentJoinKey);
        if (idx >= 0) {
            const query = {
                name: this.nearbyQueryName,
                field: node.data.joinKey,
                operator: '=',
                value: parentRecordNode.data.record[idx].toString()
            };
            return query;
        }
        return null;
    }

    public findKeyParent(node: Tree<NearbyAssetNode>) {
        let result = null;
        if (node.parent) {
            if (node.parent.data && node.parent.data.record) {
                result = node.parent;
            } else {
                result = this.findKeyParent(node.parent);
            }
        }
        return result;
    }

    public async updateLayerFilter(layer: QueryableMapCoreLayer, query: IQuery) {
        this.toSqlStringService.removeClauses(layer.mapcoreProps.query, { name: this.nearbyQueryName });
        const newQuery = this.toSqlStringService.addFilter(layer.mapcoreProps.query, query);
        this.layerService.updateLayerFilter(layer, newQuery);
    }

    public async getLayerRecords(layer: MapCoreLayer, fieldNames): Promise<{ columns: ITableColumn[]; records: string[][] }> {
        let columns;
        let records: string[][] = [];
        if (layer) {
            // TODO: Consider automatically adding GEOMETRY column to fieldNames if not
            //       already in the field list because we will need it.
            columns = this.filterColumns(layer.mapcoreProps.columns, fieldNames);
            records = await this.layerApi.getData(
                layer.mapcoreProps.parentId,
                fieldNames,
                layer.mapcoreProps.query,
                this.maxRecordsFetched
            );
        }
        columns.forEach(c => c.alias = c.alias.replace(/_/g, ' '));
        return { columns, records };
    }

    public async getTableRecords(tableId: number, query: IQuery, fieldNames: string[]): Promise<{ columns: ITableColumn[]; records: string[][] }> {
        let records: string[][] = [];
        let columns: ITableColumn[] = [];
        const filter = this.toSqlStringService.getSql(query);
        // TODO: Is there anyway to filter the columns list to just those in columnList?
        const results = await this.layerService.getTable(tableId, `id columns { name, alias, dataType, columnIdentifierId, databaseColumnOrder } records:data(fieldNames: ["${fieldNames}"], filter: "${filter}")`);
        if (results && results.length) {
            columns = this.filterColumns(results[0].columns, fieldNames);
            records = results[0].records;
        }
        columns.forEach(c => c.alias = c.alias.replace(/_/g, ' '));
        return { columns, records };
    }

    private filterColumns(columns: (ILayerColumn | ITableColumn)[], columnList): ILayerColumn[] | ITableColumn[] {
        const filtered = columns.filter(c => columnList.indexOf(c.name) >= 0);
        const sorted = filtered.sort((a, b) => columnList.indexOf(a.name) - columnList.indexOf(b.name));
        return sorted;
    }

    public getSortableColumns(node: Tree<NearbyAssetNode>) {
        // TODO: Consider removing fileName, size, source, url as well as they are not that useful for grouping ECS documents.
        const results = node.data.columns ? node.data.columns.filter(c => c.name !== 'GEOMETRY') : [];
        return results;
    }
}
